import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Chicago Opportunity Desk";
const BASE_URL = "https://joincod.org";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * Per-page SEO metadata.
 *
 *   <Seo
 *     title="Contact"
 *     description="..."
 *     path="/contact"
 *     breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
 *     faq={[{ q: "...", a: "..." }]}
 *     schema="LocalBusiness"   // or "ProfessionalService" | "Organization" | "WebSite"
 *     noindex={false}
 *   />
 */
export function Seo({
  title,
  description,
  path,
  noindex = false,
  image,
  breadcrumbs,
  faq,
  schema, // additional schema type to inject alongside the base Organization/WebSite
}) {
  const canonical = path ? `${BASE_URL}${path}` : BASE_URL;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : "Join COD | Workforce Platform";
  const desc = description || "Chicago Opportunity Desk (COD) connects Chicago residents to employment, healthcare careers, certifications, housing resources, government benefits, and workforce opportunities through one intelligent platform.";
  const ogImage = image || DEFAULT_OG_IMAGE;

  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": b.name,
      "item": `${BASE_URL}${b.path}`,
    })),
  } : null;

  const faqLd = faq && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;

  const extraSchemaLd = schema === "LocalBusiness" ? {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}#localbusiness`,
    "name": SITE_NAME,
    "url": BASE_URL,
    "logo": `${BASE_URL}/icon-512.png`,
    "image": DEFAULT_OG_IMAGE,
    "description": desc,
    "telephone": "+1-312-343-4183",
    "email": "info@joinCOD.org",
    "address": { "@type": "PostalAddress", "addressLocality": "Chicago", "addressRegion": "IL", "addressCountry": "US" },
    "areaServed": { "@type": "City", "name": "Chicago" },
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
      "opens": "09:00", "closes": "18:00",
    }],
    "priceRange": "$$",
    "sameAs": [
      "https://www.instagram.com/join.cod",
      "https://www.facebook.com/profile.php?id=61591789641024",
    ],
  } : schema === "ProfessionalService" ? {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}#professionalservice`,
    "name": SITE_NAME,
    "url": BASE_URL,
    "logo": `${BASE_URL}/icon-512.png`,
    "image": DEFAULT_OG_IMAGE,
    "description": desc,
    "areaServed": { "@type": "City", "name": "Chicago" },
    "telephone": "+1-312-343-4183",
    "email": "info@joinCOD.org",
    "sameAs": [
      "https://www.instagram.com/join.cod",
      "https://www.facebook.com/profile.php?id=61591789641024",
    ],
  } : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc}/>
      <link rel="canonical" href={canonical}/>
      {noindex && <meta name="robots" content="noindex,nofollow"/>}

      {/* Open Graph */}
      <meta property="og:type" content="website"/>
      <meta property="og:site_name" content={SITE_NAME}/>
      <meta property="og:title" content={fullTitle}/>
      <meta property="og:description" content={desc}/>
      <meta property="og:url" content={canonical}/>
      <meta property="og:image" content={ogImage}/>
      <meta property="og:image:width" content="1200"/>
      <meta property="og:image:height" content="630"/>
      <meta property="og:locale" content="en_US"/>

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image"/>
      <meta name="twitter:title" content={fullTitle}/>
      <meta name="twitter:description" content={desc}/>
      <meta name="twitter:image" content={ogImage}/>

      {/* Structured data */}
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
      {faqLd && (
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      )}
      {extraSchemaLd && (
        <script type="application/ld+json">{JSON.stringify(extraSchemaLd)}</script>
      )}
    </Helmet>
  );
}
